import React, { useRef, useState } from 'react';
import { api } from '../lib/api';
import { Receipt } from '../types';

interface ReceiptUploaderProps {
  runId: string;
  onReceiptUploaded: (receipt: Receipt) => void;
  disabled?: boolean;
}

export const ReceiptUploader: React.FC<ReceiptUploaderProps> = ({
  runId,
  onReceiptUploaded,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|png|gif)$/)) {
      setError('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const result = await api.safeUploadReceipt(runId, file);

      if (result.error) {
        setError(result.error);
      } else if (result.data) {
        onReceiptUploaded(result.data);
      }
    } catch (err) {
      setError('Failed to upload receipt. Please try again.');
      console.error('Receipt upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-coffee-500 bg-coffee-50' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-coffee-400 hover:bg-gray-50'}
          ${uploading ? 'opacity-75' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={disabled || uploading ? undefined : handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || uploading}
        />

        <div className="space-y-3">
          <div className="text-4xl">{uploading ? '⏳' : '📄'}</div>

          <div>
            <p className="text-lg font-medium text-gray-900">
              {uploading ? 'Processing receipt...' : 'Upload receipt'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {uploading
                ? 'Extracting vendor, total, and date information'
                : 'Drag and drop or click to select an image file'}
            </p>
          </div>

          <div className="text-xs text-gray-500">
            Supports JPEG, PNG, GIF • Max 10MB
          </div>

          {uploading && (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-coffee-600"></div>
              <span className="text-sm text-gray-600">
                Uploading and processing...
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ReceiptUploader;
